import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Alert, Card, Col, Row, Select, Switch, Upload, Input, Typography, Form } from 'antd';
import { FormattedMessage } from 'react-intl';
import { InboxOutlined } from '@ant-design/icons';
import AudioRecorder from './AudioRecorder';
import { LocationSearch } from './LocationSearch';

const { Option } = Select;
const { Text } = Typography;

const PLACEHOLDER_RX = /{{[^}]+}}/g;
const WHATSAPP_TAG_TO_HTML_MAP = {
	'*': '<strong>$1</strong>',
	_: '<em>$1</em>',
	'~': '<s>$1</s>',
	'```': '<code>$1</code>',
};

const convertWhatsappTagsToHTML = (text) => {
	if (!text) return '';
	let html = text;
	for (const tag in WHATSAPP_TAG_TO_HTML_MAP) {
		if (tag === '*') html = html.replace(/\*([^*]+)\*/g, WHATSAPP_TAG_TO_HTML_MAP[tag]);
		else if (tag === '_') html = html.replace(/_(.*?)_/g, WHATSAPP_TAG_TO_HTML_MAP[tag]);
		else if (tag === '~') html = html.replace(/~(.*?)~/g, WHATSAPP_TAG_TO_HTML_MAP[tag]);
		else if (tag === '```') html = html.replace(/```([\s\S]*?)```/g, WHATSAPP_TAG_TO_HTML_MAP[tag]);
	}
	return html;
};

const splitTextWithPlaceholders = (text) => {
	if (!text) return [];
	return text
		.replace(PLACEHOLDER_RX, '{{}}')
		.split(/({{}})/)
		.filter((part) => part !== '');
};

const TemplateLayout = ({ state, setState, language }) => {
	const [headerParts, setHeaderParts] = useState([]);
	const [bodyParts, setBodyParts] = useState([]);
	const [buttonParts, setButtonParts] = useState([]);
	const [footerText, setFooterText] = useState('');
	const [headerType, setHeaderType] = useState(null);
	const [headerMedia, setHeaderMedia] = useState(null);
	const [location, setLocation] = useState(null);

	const handleToggleOnChange = () => {
		const { isToggleActived } = state;
		setState({ isToggleActived: !isToggleActived, files: [] });
	};

	const handleChangeTemplate = (index) => {
		const template = state.templateList[index];
		if (!template) return;

		setLocation(null);
		const getComponent = (type) => template.components?.find((c) => c.type === type);

		const headerComp = getComponent('HEADER');
		const bodyComp = getComponent('BODY');
		const footerComp = getComponent('FOOTER');
		const buttonsComp = getComponent('BUTTONS');

		setHeaderType(headerComp?.format);
		if (
			headerComp?.format === 'IMAGE' ||
			headerComp?.format === 'DOCUMENT' ||
			headerComp?.format === 'VIDEO'
		) {
			setHeaderMedia(headerComp?.example?.header_handle?.[0]);
		}

		setHeaderParts(splitTextWithPlaceholders(headerComp?.text));
		setBodyParts(splitTextWithPlaceholders(bodyComp?.text));
		setButtonParts(
			(buttonsComp?.buttons || []).map((b) => ({
				original: b,
				text: splitTextWithPlaceholders(b.text),
				variables:
					b.type === 'COPY_CODE' || b.type === 'VOICE_CALL'
						? ['{{}}']
						: b.url
							? splitTextWithPlaceholders(b.url)
							: [],
			})),
		);
		setFooterText(footerComp?.text ?? '');

		setState({
			...state,
			selectedTemplate: template,
			selectedTemplateArray: [],
		});
	};

	const resetTemplateList = () => {
		if (state.fromApi === 'messageBird') {
			setState({
				selectedTemplate: { name: '' },
				selectedTemplateArray: [],
				buttonsVars: [],
			});
		}
	};

	const dummyRequest = ({ onSuccess }) => {
		setTimeout(() => {
			onSuccess('ok');
		}, 0);
	};

	const beforeUpload = (file) => {
		if (file.size > 104857600) {
			setState({ maxFileSize: true });
		} else {
			setState({ maxFileSize: false });
		}

		const files = [file];
		setState({ files });
		resetTemplateList();
	};

	const removeFile = (file) => {
		const { files } = state;
		const newFiles = files.filter((ele) => ele !== file);
		setState({ files: newFiles });
	};

	const handleLocation = useCallback(
		(loc) => {
			setLocation(loc);

			const locationParameter = {
				latitude: loc.latitude,
				longitude: loc.longitude,
				name: loc.name,
				address: loc.address,
			};

			setState((prev) => {
				const updatedTemplate = {
					...prev.selectedTemplate,
					components: prev.selectedTemplate.components.map((component) =>
						component.type === 'HEADER' && component.format === 'LOCATION'
							? {
									...component,
									location: locationParameter,
								}
							: component,
					),
				};

				return {
					...prev,
					selectedTemplate: updatedTemplate,
					headerParameters: [locationParameter],
				};
			});
		},
		[setState],
	);

	const renderTextInput = useCallback(
		(parts, startIndex) => {
			let paramCursor = startIndex;
			const nodes = [];

			parts.forEach((part, idx) => {
				if (part === '{{}}') {
					const currentParamIndex = paramCursor;
					paramCursor += 1;

					nodes.push(
						<Form.Item
							key={`input-${currentParamIndex}`}
							style={{ display: 'inline-block', margin: 0, padding: 0, verticalAlign: 'middle' }}
						>
							<Input
								size="small"
								style={{
									minWidth: 80,
									display: 'inline-block',
									height: 22,
									fontSize: 14,
								}}
								placeholder={`Parâmetro ${currentParamIndex + 1}`}
								value={state.selectedTemplateArray[currentParamIndex] || ''}
								onChange={(e) => {
									const newParams = [...state.selectedTemplateArray];
									newParams[currentParamIndex] = e.target.value;
									setState({ ...state, selectedTemplateArray: newParams });
								}}
							/>
						</Form.Item>,
					);
				} else {
					nodes.push(
						<span
							key={`text-${startIndex}-${idx}`}
							style={{ fontSize: '14px', display: 'inline', verticalAlign: 'middle' }}
							dangerouslySetInnerHTML={{ __html: convertWhatsappTagsToHTML(part) }}
						/>,
					);
				}
			});

			return { nodes, consumed: paramCursor - startIndex };
		},
		[state, setState],
	);

	const headerRendered = useMemo(
		() => renderTextInput(headerParts, 0),
		[headerParts, renderTextInput],
	);
	const bodyRendered = useMemo(
		() => renderTextInput(bodyParts, headerRendered.consumed),
		[bodyParts, headerRendered.consumed, renderTextInput],
	);

	const buttonParamsCount = useMemo(() => {
		let count = 0;
		buttonParts.forEach((btn) => {
			btn.variables.forEach((v) => {
				if (v === '{{}}') count++;
			});
		});
		return count;
	}, [buttonParts]);

	let buttonParamsCursor = headerRendered.consumed + bodyRendered.consumed;

	const totalParams = useMemo(() => {
		return headerRendered.consumed + bodyRendered.consumed + buttonParamsCount;
	}, [headerRendered.consumed, bodyRendered.consumed, buttonParamsCount]);

	useEffect(() => {
		setState((prev) => {
			if (prev.paramsCount !== totalParams) {
				return { ...prev, paramsCount: totalParams };
			}
			return prev;
		});
	}, [totalParams]);

	return (
		<Row>
			<Col span={24}>
				<Row>
					<Switch style={{ marginRight: '10px' }} onChange={handleToggleOnChange} />
					<FormattedMessage id="toggle_mbird" />
					{state.isToggleActived && (
						<Alert
							message={<FormattedMessage id="alert_audio" />}
							type="error"
							closable
							style={{ marginTop: '8px' }}
						/>
					)}
				</Row>

				<Row>
					<Col span={24}>
						<Select
							showSearch
							optionFilterProp="children"
							style={{ width: '100%', margin: '5px 0' }}
							placeholder={language.template}
							onChange={handleChangeTemplate}
							disabled={!!state.templateListError || state.files.length > 0}
							value={state.selectedTemplate.name}
						>
							{state.templateList.map((element, index) => (
								<Option key={element.id} value={index}>
									{element.name + ' - ' + (element.languageDescription ?? element.language)}
								</Option>
							))}
						</Select>

						{state.templateListError && (
							<Alert
								message={language.template_error}
								description={
									<div>
										<p>{state.templateListError?.message || language.unknown_error}</p>
										<p style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
											<strong>{language.possible_cause}:</strong>{' '}
											{language.template_fetch_permission_error}
										</p>
									</div>
								}
								type="error"
								showIcon
								style={{ marginTop: '10px' }}
							/>
						)}

						{state.selectedTemplate.name && (
							<Card
								title="Modelo"
								headStyle={{
									backgroundColor: '#075E54',
									color: 'white',
									borderRadius: '6px 6px 0 0',
									textAlign: 'left',
								}}
								bodyStyle={{
									backgroundColor: '#ece5dd',
									borderRadius: '0 0 6px 6px',
									textAlign: 'left',
								}}
								style={{ width: '100%', marginTop: 16 }}
							>
								<div
									style={{
										backgroundColor: 'white',
										borderRadius: 12,
										padding: '12px 16px',
										maxWidth: '800px',
									}}
								>
									{/* Cabeçalho */}
									{(headerParts.length > 0 || headerType) && (
										<div
											style={{
												fontSize: 14,
												paddingBottom: 8,
												borderBottom: '1px solid #e0e0e0',
												width: '100%',
											}}
										>
											{headerParts.length > 0 && headerRendered.nodes}

											{headerType === 'IMAGE' && (
												<div style={{ display: 'flex', justifyContent: 'center' }}>
													<img
														src={headerMedia}
														alt="Header"
														style={{
															width: '400px',
															height: '400px',
															objectFit: 'cover',
														}}
													/>
												</div>
											)}

											{headerType === 'VIDEO' && (
												<div style={{ display: 'flex', justifyContent: 'center' }}>
													<video
														src={headerMedia}
														controls
														style={{
															width: '400px',
															height: '400px',
															objectFit: 'cover',
														}}
													/>
												</div>
											)}

											{headerType === 'DOCUMENT' && (
												<a
													href={headerMedia}
													target="_blank"
													rel="noopener noreferrer"
													style={{
														display: 'flex',
														justifyContent: 'center',
														marginTop: 8,
													}}
												>
													<div
														style={{
															color: '#0088cc',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															fontWeight: 500,
														}}
													>
														<FormattedMessage id="view_header_document" />
													</div>
												</a>
											)}

											{headerType === 'LOCATION' && (
												<div
													style={{
														display: 'flex',
														flexDirection: 'column',
														width: '100%',
														gap: 8,
													}}
												>
													<LocationSearch onSelect={handleLocation} />

													{location && (
														<div
															style={{
																border: '1px solid #ddd',
																borderRadius: 8,
																padding: 10,
																backgroundColor: '#fafafa',
																width: '100%',
															}}
														>
															<div style={{ fontWeight: 600 }}>📍 {location.name}</div>

															{location.address && (
																<div style={{ fontSize: 13, opacity: 0.85 }}>
																	{location.address}
																</div>
															)}

															<div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
																Lat: {location.latitude} • Lng: {location.longitude}
															</div>
														</div>
													)}
												</div>
											)}
										</div>
									)}
									{/* Corpo */}
									<div style={{ fontSize: 14, marginBottom: 8 }}>{bodyRendered.nodes}</div>
									{/* Rodapé */}
									{footerText && (
										<Text type="secondary" style={{ fontSize: 12 }}>
											{convertWhatsappTagsToHTML(footerText)}
										</Text>
									)}
									{/* Renderiza Botões */}
									{buttonParts.length > 0 && (
										<div
											style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}
										>
											{buttonParts.map((button, bidx) => {
												const urlRendered = button.variables.length
													? renderTextInput(button.variables, buttonParamsCursor)
													: { nodes: [], consumed: 0 };
												const consumedUrl = urlRendered.consumed;
												buttonParamsCursor += consumedUrl;

												return (
													<div
														key={`btn-${bidx}`}
														style={{
															textDecoration: 'none',
															fontSize: 14,
															color: '#0088cc',
															borderTop: '1px solid #eee',
															display: 'flex',
															flexDirection: 'row',
															alignItems: 'center',
															justifyContent: 'center',
															paddingTop: 5,
															paddingBottom: 0,
															marginBottom: 0,
														}}
													>
														<span style={{ display: 'flex', alignItems: 'center' }}>
															<span>
																{button.text}
																{urlRendered.nodes.filter((node) => node.type !== 'span')}
															</span>
														</span>
													</div>
												);
											})}
										</div>
									)}
								</div>
							</Card>
						)}
					</Col>

					{state.isToggleActived && (
						<>
							<Col span={21} style={{ marginTop: 5 }}>
								<Upload.Dragger
									name="files"
									customRequest={dummyRequest}
									fileList={state.files}
									beforeUpload={beforeUpload}
									showUploadList={{
										showDownloadIcon: false,
										showRemoveIcon: true,
									}}
									onRemove={removeFile}
								>
									<p className="ant-upload-drag-icon">
										<InboxOutlined />
									</p>
									<p className="ant-upload-text">{language.files_text1}</p>
									<p className="ant-upload-hint">{language.files_text2}</p>
								</Upload.Dragger>
							</Col>

							<AudioRecorder state={state} setState={setState} />

							<Col span={24} style={{ marginTop: 5 }}>
								{state.maxFileSize === true && (
									<div style={{ padding: 5 }}>
										<Alert
											message={<FormattedMessage id="warning_maximum_file_size" />}
											type="error"
											showIcon
										/>
									</div>
								)}
							</Col>
						</>
					)}
				</Row>
			</Col>
		</Row>
	);
};

export default TemplateLayout;
